import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useState } from "react";

const FormSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Valid email required").required("Email required"),
  phone_number: yup.string().required("Phone number is required"),
  business_type: yup.string().required("Business type is required"),
  message: yup.string().required("Tell us a bit about your project"),
});

function ContactPage() {
    const [apiResponse, setApiResponse] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone_number: "",
      business_type: "",
      message: "",
    },
    validationSchema: FormSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const apiBaseUrl = (process.env.REACT_APP_API_URL || "").trim();

      if (!apiBaseUrl) {
        setApiResponse({
          type: "error",
          message: "API URL is not configured.",
        });
        setSubmitting(false);
        return;
      }

      try {
        setApiResponse(null);
        await axios.post(`${apiBaseUrl}/contact-us`, {
          name: values.name,
          email: values.email,
          phone_number: values.phone_number,
          business_type: values.business_type,
          message: values.message,
        });
        resetForm();
        setApiResponse({
          type: "success",
          message: "Message sent successfully.",
        });
      } catch (error: unknown) {
        const backendMessage = axios.isAxiosError(error)
          ? (error.response?.data as { message?: string } | undefined)?.message
          : undefined;

        setApiResponse({
          type: "error",
          message: backendMessage || "Failed to send message.",
        });
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });
  return (
    <div className="min-h-screen bg-white text-black">
      <Navbar />
      <main>
        <section className="page-hero">
          <div className="container">
            <span className="eyebrow">Contact | Partner With Us</span>
            <h1>Let’s build the future together</h1>
            <p>
              Connect with ThoughtCodes to discuss digital modernization,
              software development, cloud migration, DevSecOps, cybersecurity,
              or AI-enabled automation initiatives.
            </p>
          </div>
        </section>

        <section className="section">
          <div className="container grid-2">
            <div className="flex flex-col gap-4">
              <div className="card">
                <h3>Get in Touch</h3>
                <p className="text-sm mb-4">
                  Have a project, partnership opportunity, or question? Connect
                  with our team to discuss how ThoughtCodes can support your
                  mission.
                </p>
                <ul className="space-y-3 text-left text-slate-700">
                  <li className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-5 w-5 text-[#2196F3]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.8}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 7.5v9a2.25 2.25 0 01-2.25 2.25h-15A2.25 2.25 0 012.25 16.5v-9m19.5 0A2.25 2.25 0 0019.5 5.25h-15A2.25 2.25 0 002.25 7.5m19.5 0v.243a2.25 2.25 0 01-.97 1.855l-7.5 5.25a2.25 2.25 0 01-2.56 0l-7.5-5.25a2.25 2.25 0 01-.97-1.855V7.5"
                      />
                    </svg>
                    <a
                      className="hover:text-[#2196F3]"
                      href="mailto:info@thoughtcodes.com"
                    >
                      info@thoughtcodes.com
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-5 w-5 text-[#2196F3]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.8}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 4.5a1.5 1.5 0 011.5-1.5h2.25a1.5 1.5 0 011.5 1.29l.39 2.73a1.5 1.5 0 01-.43 1.3l-1.12 1.12a12 12 0 005.66 5.66l1.12-1.12a1.5 1.5 0 011.3-.43l2.73.39a1.5 1.5 0 011.29 1.5v2.25a1.5 1.5 0 01-1.5 1.5h-.75C9.31 21.75 2.25 14.69 2.25 6V4.5z"
                      />
                    </svg>
                    <a className="hover:text-[#2196F3]" href="tel:+7032312569">
                      + (703) 231-2569
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-5 w-5 text-[#2196F3]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.8}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 21s-6.75-5.625-6.75-11.25A6.75 6.75 0 1118.75 9.75C18.75 15.375 12 21 12 21z"
                      />
                      <circle cx="12" cy="9.75" r="2.25" />
                    </svg>
                    <span>4705 Eisenhower Ave Ste A, Alexandria, VA 22304</span>
                  </li>
                </ul>
                <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
                  <iframe
                    title="ThoughtCodes office location map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3109.243335546798!2d-77.1191057234128!3d38.803977352210026!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7b230597f005f%3A0x568e9455d9ea90f9!2s4705%20Eisenhower%20Ave%20a%2C%20Alexandria%2C%20VA%2022304%2C%20USA!5e0!3m2!1sen!2sin!4v1776662609913!5m2!1sen!2sin"
                    width="100%"
                    height="150"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
              <div className="card">
                <h3>How We Can Support You</h3>
                <ul className="list-check">
                  <li>Federal and public sector teaming partnerships</li>
                  <li>Custom software development and systems modernization</li>
                  <li>Cloud migration and DevSecOps implementation</li>
                  <li>
                    Cybersecurity and compliance support (NIST, FedRAMP, FISMA)
                  </li>
                  <li>AI-enabled automation and workflow optimization</li>
                </ul>
              </div>
            </div>
            <div className="card">
              <h3>Start the Conversation</h3>
              <p className="text-sm mb-4">
                Tell us about your needs, and our team will follow up promptly.
              </p>
              <form onSubmit={formik.handleSubmit} className="relative">
                <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/70 via-transparent to-transparent" />
                <div className="relative space-y-5">
                  {/* Name + Email */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
                        Your name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-1 focus:ring-indigo-300/40"
                        placeholder="Your Name Here"
                      />
                      {formik.touched.name && formik.errors.name && (
                        <p className="mt-1.5 text-[11px] !text-rose-400">
                          {formik.errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
                        Your email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-1 focus:ring-indigo-300/40"
                        placeholder="you@company.com"
                      />
                      {formik.touched.email && formik.errors.email && (
                        <p className="mt-1.5 text-[11px] !text-rose-400">
                          {formik.errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone + Business type */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        name="phone_number"
                        value={formik.values.phone_number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-1 focus:ring-indigo-300/40"
                        placeholder="+1 (555) 000-0000"
                      />
                      {formik.touched.phone_number &&
                        formik.errors.phone_number && (
                          <p className="mt-1.5 text-[11px] !text-rose-400">
                            {formik.errors.phone_number}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
                        Business type
                      </label>
                      <input
                        type="text"
                        name="business_type"
                        value={formik.values.business_type}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-1 focus:ring-indigo-300/40"
                        placeholder="e.g. Startup, Enterprise, Agency"
                      />
                      {formik.touched.business_type &&
                        formik.errors.business_type && (
                          <p className="mt-1.5 text-[11px] !text-rose-400">
                            {formik.errors.business_type}
                          </p>
                        )}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      rows={4}
                      className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-1 focus:ring-indigo-300/40"
                      placeholder="Briefly describe your project, goals, or areas where you need support."
                    />
                    {formik.touched.message && formik.errors.message && (
                      <p className="mt-1.5 text-[11px] !text-rose-400">
                        {formik.errors.message}
                      </p>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl button-gradient px-10 py-3 text-base font-medium text-white"
                  >
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/20 via-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <span className="relative">
                      {formik.isSubmitting ? "Sending..." : "Send message"}
                    </span>
                    <svg
                      className="relative h-4 w-4 transition-transform group-hover:translate-x-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>

                  {apiResponse && (
                    <p
                      className={`text-sm ${
                        apiResponse.type === "success"
                          ? "text-emerald-600"
                          : "text-rose-600"
                      }`}
                    >
                      {apiResponse.message}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ContactPage;
